import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of, tap } from 'rxjs';

/**
 * In-memory cache for GET API responses used by the home page.
 * Cached entries expire after CACHE_TTL milliseconds.
 */
interface CacheEntry {
  response: HttpResponse<unknown>;
  timestamp: number;
}

const cache = new Map<string, CacheEntry>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/** API path segments that should be cached (home page data sources) */
const CACHEABLE_API_SEGMENTS = [
  'api/events',
  'api/news',
  'api/dspace',
  'api/ComplexAttachements',
  'api/statistics',
  'api/about',
  'api/partners',
  'api/centers',
  'api/services',
];

function isCacheableRequest(url: string): boolean {
  return CACHEABLE_API_SEGMENTS.some((segment) =>
    url.toLowerCase().includes(segment.toLowerCase())
  );
}

export const cacheInterceptor: HttpInterceptorFn = (req, next) => {
  // Only cache GET requests
  if (req.method !== 'GET') {
    return next(req);
  }

  // Only cache requests to home-page API endpoints
  if (!isCacheableRequest(req.urlWithParams)) {
    return next(req);
  }

  const cacheKey = req.urlWithParams;

  // Check for a valid cached response
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return of(cached.response.clone());
  }

  // Forward the request and cache the response
  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        cache.set(cacheKey, {
          response: event.clone(),
          timestamp: Date.now(),
        });
      }
    })
  );
};
